import { closestCenter, DndContext, type DragEndEvent, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useEffect, useState } from "react";
import { GripVertical, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";

import { trpc } from "~/api/trpc";

type OrderableRoom = {
    id: number;
    name: string;
    sequence: number;
    room_category: { id: number; name: string } | null;
};

type RoomOrderDialogProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
};

const SortableRoomItem = ({ room, position }: { room: OrderableRoom; position: number }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: room.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <li
            ref={setNodeRef}
            style={style}
            className="flex items-center gap-3 rounded-md border bg-white px-3 py-2"
        >
            <button
                type="button"
                className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground"
                {...attributes}
                {...listeners}
            >
                <GripVertical className="h-4 w-4" />
            </button>
            <span className="w-6 text-sm text-muted-foreground tabular-nums">{position}.</span>
            <span className="flex-1 font-medium">{room.name}</span>
            {room.room_category && (
                <span className="text-xs text-muted-foreground">{room.room_category.name}</span>
            )}
        </li>
    );
};

const RoomOrderDialog = ({ open, onOpenChange }: RoomOrderDialogProps) => {
    const utils = trpc.useUtils();
    const { data: stores } = trpc.stores.getAllSimple.useQuery(undefined, { enabled: open });

    const [storeId, setStoreId] = useState<number | null>(null);
    const [orderedRooms, setOrderedRooms] = useState<OrderableRoom[]>([]);

    useEffect(() => {
        if (open && storeId === null && stores && stores.length > 0) {
            setStoreId(stores[0].id);
        }
    }, [open, storeId, stores]);

    const { data: rooms, isLoading } = trpc.rooms.getByStoreId.useQuery(
        { storeId: storeId! },
        { enabled: open && storeId !== null }
    );

    useEffect(() => {
        if (rooms) {
            setOrderedRooms(rooms as OrderableRoom[]);
        }
    }, [rooms]);

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

    const reorderMutation = trpc.rooms.reorder.useMutation({
        onSuccess: () => {
            if (storeId !== null) {
                utils.rooms.getByStoreId.invalidate({ storeId });
            }
            utils.rooms.getAll.invalidate();
        },
        onError: (error) => {
            toast.error("Fehler beim Sortieren", { description: error.message });
            if (storeId !== null) {
                utils.rooms.getByStoreId.invalidate({ storeId });
            }
        },
    });

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const oldIndex = orderedRooms.findIndex((r) => r.id === active.id);
        const newIndex = orderedRooms.findIndex((r) => r.id === over.id);
        const reordered = arrayMove(orderedRooms, oldIndex, newIndex);
        setOrderedRooms(reordered);

        reorderMutation.mutate({
            orders: reordered.map((r, idx) => ({ id: r.id, sequence: idx + 1 })),
        });
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-h-[85vh] flex flex-col">
                <DialogHeader>
                    <DialogTitle>Reihenfolge der Räume</DialogTitle>
                    <DialogDescription>
                        Legt fest, in welcher Reihenfolge die Räume im Live-Board angezeigt werden. Reihenfolge per Drag & Drop ändern.
                    </DialogDescription>
                </DialogHeader>

                <div className="flex flex-col gap-2">
                    <Label htmlFor="roomOrderStore">Filiale</Label>
                    <Select
                        value={storeId?.toString() ?? ""}
                        onValueChange={(value) => setStoreId(parseInt(value, 10))}
                    >
                        <SelectTrigger id="roomOrderStore">
                            <SelectValue placeholder="Filiale wählen" />
                        </SelectTrigger>
                        <SelectContent>
                            {stores?.map((store) => (
                                <SelectItem key={store.id} value={store.id.toString()}>
                                    {store.name}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <div className="flex-1 overflow-y-auto min-h-0 py-2">
                    {isLoading || storeId === null ? (
                        <div className="flex flex-col gap-2">
                            {Array.from({ length: 4 }).map((_, i) => (
                                <Skeleton key={i} className="h-10 w-full" />
                            ))}
                        </div>
                    ) : orderedRooms.length > 0 ? (
                        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                            <SortableContext items={orderedRooms.map((r) => r.id)} strategy={verticalListSortingStrategy}>
                                <ul className="flex flex-col gap-2">
                                    {orderedRooms.map((room, idx) => (
                                        <SortableRoomItem key={room.id} room={room} position={idx + 1} />
                                    ))}
                                </ul>
                            </SortableContext>
                        </DndContext>
                    ) : (
                        <div className="text-center py-8 text-muted-foreground">
                            Keine Räume in dieser Filiale.
                        </div>
                    )}
                </div>

                <DialogFooter className="items-center">
                    {reorderMutation.isPending && (
                        <span className="flex items-center text-sm text-muted-foreground mr-auto">
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Speichern...
                        </span>
                    )}
                    <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                        Schließen
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default RoomOrderDialog;
